'use client';
import 'swiper/css';
import 'swiper/css/navigation';
import './swiper-overrides.css';

import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { PageCard } from '@/components/Card/PageCard';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import {
  ComponentBlocksRelatedArticles,
  Enum_Componentshareddisplayoptions_Contentswidth,
  Page,
} from '@/types';

import { CustomBackground } from '../CustomBackground/CustomBackground';
import { ParsePillHeading } from '../ParsePillHeading';

function RelatedArticlesComponent(props: ComponentBlocksRelatedArticles) {
  const Pages = props.Pages as Array<Page>;
  const uniquePages = React.useMemo(() => {
    const seen = new Set<string>();
    return Pages.filter((page) => {
      const slug = page?.Slug;
      if (!slug || seen.has(slug)) return false;
      seen.add(slug);
      return true;
    });
  }, [Pages]);

  const renderCard = (article: Page) => {
    const imageUrl =
      getImageUrl({ image: article?.PageHero?.HeroMedia, size: 'url' }) ||
      getImageUrl({
        image: article?.PageHero?.HeroBackgroundImage,
        size: 'url',
      }) ||
      getImageUrl({ image: article?.seo?.metaImage, size: 'url' }) ||
      '/assets/placeholder-thumbnail.png';

    const paragraph =
      article?.PageHero?.HeroText?.filter((block: any) => block.type === 'paragraph')
        ?.map((block: any) => block.children?.map((child: any) => child.text).join('') || '')
        ?.join('\n') || '';

    return (
      <PageCard
        key={article?.Slug}
        slug={`${article?.Slug}`}
        title={`${article?.Title}`}
        paragraph={paragraph}
        imageUrl={imageUrl}
        publishedAt={article?.publishedAt}
      />
    );
  };

  return (
    <CustomBackground
      backgroundOptions={(props?.RelatedArticlesBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        (props?.RelatedArticlesDisplayOptions ?? {
          ContentsWidth: Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: 'md',
          PaddingBottom: 'md',
          PaddingInline: 'md',
        }) as StrapiDisplayOptions | undefined
      }
      boxProps={{
        // @ts-expect-error - actually does support data props
        'data-component': 'RelatedArticles',
      }}
    >
      <Box sx={{ mx: 'auto' }}>
        <ParsePillHeading title="_Recent_ Articles" />
        <Typography variant="h6" fontWeight={700} mb={3}>
          You might also like
        </Typography>

        {uniquePages.length > 3 ? (
          <Swiper
            modules={[Navigation]}
            navigation
            spaceBetween={24}
            slidesPerView={1.1}
            style={{ paddingBottom: '2px' }}
            breakpoints={{
              600: { slidesPerView: 2.1 },
              900: { slidesPerView: 3 },
            }}
          >
            {uniquePages.map((article) => (
              <SwiperSlide key={article?.Slug} style={{ height: 'auto' }}>
                {renderCard(article)}
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2, sm: 3 }}
            justifyContent="space-between"
            sx={{
              p: 1,
              pb: 2,
            }}
          >
            {uniquePages.map((article) => (
              <Box key={article?.Slug} sx={{ flex: 1, minWidth: 0 }}>
                {renderCard(article)}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </CustomBackground>
  );
}

export default function RelatedArticles(props: ComponentBlocksRelatedArticles) {
  const { Pages } = props;
  if (!Array.isArray(Pages) || Pages.length === 0) return null;

  return <RelatedArticlesComponent {...props} />;
}
